import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../services/api';

const FeedbackModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    type: 'bug',
    title: '',
    description: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const recordingTimerRef = useRef(null);

  const feedbackTypes = [
    { value: 'bug', label: 'Bug Report', icon: '🐛' },
    { value: 'feature_request', label: 'Feature Request', icon: '💡' },
    { value: 'question_suggestion', label: 'Question Suggestion', icon: '❓' },
    { value: 'doubt', label: 'Doubt', icon: '🤔' },
    { value: 'general_message', label: 'General Message', icon: '💬' }
  ];

  // Cleanup audio URL on unmount or when audio changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Stop recording timer on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioBlob(audioBlob);
        setAudioUrl(audioUrl);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Unable to access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const playAudio = () => {
    if (audioRef.current && audioUrl) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const deleteAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setIsPlaying(false);
    setRecordingTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file size (10MB max)
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        setError(`File "${file.name}" exceeds 10MB limit`);
        return false;
      }
      return true;
    });

    // Check total files
    if (attachments.length + validFiles.length > 10) {
      setError('Maximum 10 files allowed');
      return;
    }

    setAttachments(prev => [...prev, ...validFiles]);
    setError('');
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return '🖼️';
    if (file.type.startsWith('video/')) return '🎥';
    if (file.type === 'application/pdf') return '📄';
    return '📎';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug: Log attachments state
    console.log('Submitting feedback with attachments:', attachments.map((a, i) => ({
      index: i,
      isFile: a instanceof File,
      isBlob: a instanceof Blob,
      type: a.constructor.name,
      size: a.size,
      name: a.name
    })));
    
    // Validate title
    if (!formData.title.trim()) {
      setError('Please provide a title');
      return;
    }

    // For doubt type, check if there's text or audio
    if (formData.type === 'doubt') {
      if (!formData.description.trim() && !audioBlob) {
        setError('Please provide either a text doubt or record an audio question');
        return;
      }
    } else {
      // For other types, description is required
      if (!formData.description.trim()) {
        setError('Please provide a description');
        return;
      }
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login to submit feedback');
        setIsSubmitting(false);
        return;
      }

      // Create FormData for file upload - ensure it's completely fresh
      const submitData = new FormData();
      
      // Explicitly add only the fields we need
      if (formData.type) submitData.append('type', String(formData.type));
      if (formData.title) submitData.append('title', String(formData.title));
      
      // For doubt type with no description, send a placeholder
      const descriptionValue = formData.description.trim() || 
        (formData.type === 'doubt' && audioBlob ? 'Audio doubt submission' : '');
      if (descriptionValue) submitData.append('description', String(descriptionValue));
      
      // Append all attachments (ensure they are File objects)
      attachments.forEach((file, index) => {
        if (file instanceof File || file instanceof Blob) {
          submitData.append('attachments', file);
        } else {
          console.warn(`Skipping attachment ${index} - not a File object:`, file);
        }
      });

      // Append audio recording if exists (for doubt type)
      if (audioBlob && formData.type === 'doubt') {
        const audioFile = new File([audioBlob], `audio-doubt-${Date.now()}.webm`, { type: 'audio/webm' });
        submitData.append('audioRecording', audioFile);
      }

      // Debug: Log FormData contents
      console.log('=== FormData being sent ===');
      const formDataEntries = [];
      for (let [key, value] of submitData.entries()) {
        if (value instanceof File) {
          formDataEntries.push({
            key,
            type: 'File',
            name: value.name,
            size: value.size,
            mimeType: value.type
          });
          console.log(`  ${key}: [File] ${value.name} (${value.size} bytes, ${value.type})`);
        } else {
          formDataEntries.push({
            key,
            type: 'string',
            value: typeof value === 'string' && value.length > 50 ? value.substring(0, 50) + '...' : value
          });
          console.log(`  ${key}: ${value}`);
        }
      }
      console.log('Total entries:', formDataEntries.length);
      console.log('Entry keys:', formDataEntries.map(e => e.key));
      console.log('========================\n');

      const response = await axios.post(
        `${BASE_URL}/api/feedback/submit`,
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
            // Don't set Content-Type - let axios set it with boundary
          }
        }
      );

      // Reset form
      setFormData({ type: 'bug', title: '', description: '' });
      setAttachments([]);
      deleteAudio(); // Clean up audio
      
      if (onSuccess) {
        onSuccess(response.data.feedback);
      }
      
      onClose();
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(
        err.response?.data?.message || 
        'Failed to submit feedback. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Submit Message
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#718096',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Feedback Type */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#2d3748'
            }}>
              Feedback Type *
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '10px'
            }}>
              {feedbackTypes.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: type.value }))}
                  style={{
                    padding: '12px',
                    border: formData.type === type.value ? '2px solid #4299e1' : '1px solid #e2e8f0',
                    borderRadius: '8px',
                    backgroundColor: formData.type === type.value ? '#ebf8ff' : '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: formData.type === type.value ? '#2c5282' : '#4a5568',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (formData.type !== type.value) {
                      e.currentTarget.style.borderColor = '#cbd5e0';
                      e.currentTarget.style.backgroundColor = '#f7fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (formData.type !== type.value) {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.backgroundColor = '#fff';
                    }
                  }}
                >
                  <span style={{ fontSize: '18px' }}>{type.icon}</span>
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#2d3748'
            }}>
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Brief summary of your feedback"
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#2d3748',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4299e1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#2d3748'
            }}>
              Description {formData.type === 'doubt' ? '(Optional if audio is provided)' : '*'}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={formData.type === 'doubt' 
                ? "Type your doubt here or record an audio..." 
                : "Provide detailed information..."}
              required={formData.type !== 'doubt' || !audioBlob}
              rows={6}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#2d3748',
                outline: 'none',
                transition: 'border-color 0.2s',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4299e1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Audio Recording for Doubt Type */}
          {formData.type === 'doubt' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#2d3748'
              }}>
                🎤 Audio Question (Optional)
              </label>
              
              {!audioBlob ? (
                <div style={{
                  border: '2px dashed #cbd5e0',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: isRecording ? '#fef5e7' : 'transparent'
                }}>
                  {!isRecording ? (
                    <>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎙️</div>
                      <div style={{ fontSize: '14px', color: '#4a5568', marginBottom: '12px' }}>
                        Record your doubt as an audio message
                      </div>
                      <button
                        type="button"
                        onClick={startRecording}
                        style={{
                          padding: '10px 20px',
                          border: 'none',
                          borderRadius: '8px',
                          backgroundColor: '#48bb78',
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#38a169'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#48bb78'}
                      >
                        🎤 Start Recording
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={{ 
                        fontSize: '48px', 
                        marginBottom: '8px',
                        animation: 'pulse 1.5s infinite'
                      }}>
                        🔴
                      </div>
                      <style>
                        {`
                          @keyframes pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.5; }
                          }
                        `}
                      </style>
                      <div style={{ 
                        fontSize: '20px', 
                        fontWeight: '600',
                        color: '#e53e3e', 
                        marginBottom: '8px' 
                      }}>
                        Recording... {formatTime(recordingTime)}
                      </div>
                      <div style={{ fontSize: '13px', color: '#718096', marginBottom: '12px' }}>
                        Speak clearly into your microphone
                      </div>
                      <button
                        type="button"
                        onClick={stopRecording}
                        style={{
                          padding: '10px 20px',
                          border: 'none',
                          borderRadius: '8px',
                          backgroundColor: '#e53e3e',
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c53030'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#e53e3e'}
                      >
                        ⏹️ Stop Recording
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: '#f0fff4'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '24px' }}>🎵</span>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                          Audio Recorded
                        </div>
                        <div style={{ fontSize: '12px', color: '#718096' }}>
                          Duration: {formatTime(recordingTime)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={deleteAudio}
                      style={{
                        background: 'none',
                        border: '1px solid #fc8181',
                        color: '#e53e3e',
                        cursor: 'pointer',
                        padding: '6px 12px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff5f5';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                  
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={() => setIsPlaying(false)}
                    style={{ display: 'none' }}
                  />
                  
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center'
                  }}>
                    {!isPlaying ? (
                      <button
                        type="button"
                        onClick={playAudio}
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: '#4299e1',
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#3182ce'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4299e1'}
                      >
                        ▶️ Play Audio
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={pauseAudio}
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: '#f59e0b',
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f59e0b'}
                      >
                        ⏸️ Pause Audio
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* File Upload */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#2d3748'
            }}>
              Attachments (Images, Videos, PDFs)
            </label>
            <div style={{
              border: '2px dashed #cbd5e0',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#4299e1';
              e.currentTarget.style.backgroundColor = '#f7fafc';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = '#cbd5e0';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={() => document.getElementById('fileInput').click()}
            >
              <input
                id="fileInput"
                type="file"
                multiple
                accept="image/*,video/*,application/pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📎</div>
              <div style={{ fontSize: '14px', color: '#4a5568', marginBottom: '4px' }}>
                Click to upload files
              </div>
              <div style={{ fontSize: '12px', color: '#718096' }}>
                Max 10MB per file, up to 10 files
              </div>
            </div>

            {/* Attachment Preview */}
            {attachments.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                {attachments.map((file, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    backgroundColor: '#f7fafc'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                      <span style={{ fontSize: '20px' }}>{getFileIcon(file)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#2d3748',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {file.name}
                        </div>
                        <div style={{ fontSize: '11px', color: '#718096' }}>
                          {formatFileSize(file.size)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#e53e3e',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        fontSize: '18px',
                        borderRadius: '4px'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fff5f5',
              border: '1px solid #fc8181',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#c53030'
            }}>
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '10px 20px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: '#4a5568',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: isSubmitting ? '#cbd5e0' : '#4299e1',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                if (!isSubmitting) e.currentTarget.style.backgroundColor = '#3182ce';
              }}
              onMouseOut={(e) => {
                if (!isSubmitting) e.currentTarget.style.backgroundColor = '#4299e1';
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
